import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../Component/server';
import LiveDetailDialog from '../LiveList/Component/LiveDetailDialog';
import { BankExcelMapping, BankExcelTemplate, SellerBankAccount } from '../../../Component/Interface/Store';

type AccountDraft = Omit<SellerBankAccount, 'seq' | 'store_seq' | 'excel_mapping'> & { seq?: number };
type TemplateDraft = { seq?: number; name: string; mapping: BankExcelMapping | null };
type SampleSheet = { name: string; rows: unknown[][] };
const emptyAccount = (): AccountDraft => ({ label: '', bank_name: '', bank_num: '', depositor: '', excel_template_seq: null, active: true });
const emptyTemplate = (): TemplateDraft => ({ name: '', mapping: null });
const jsonConfig = { headers: { 'Content-Type': 'application/json' } };
const mappingFields = [
  { key: 'depositor_column', label: '입금자명 열' },
  { key: 'amount_column', label: '입금액 열' },
] as const;
const errorText = (cause: any, fallback: string) => {
  const message = cause?.response?.data?.message;
  return Array.isArray(message) ? message.join(' ') : typeof message === 'string' ? message : fallback;
};

export default function BankAccounts({ disabled = false }: { disabled?: boolean }) {
  const [open, setOpen] = useState<'templates' | 'accounts' | null>(null);
  const [discard, setDiscard] = useState<'dialog' | 'editor' | null>(null);
  const [accounts, setAccounts] = useState<SellerBankAccount[]>([]);
  const [templates, setTemplates] = useState<BankExcelTemplate[]>([]);
  const [accountDraft, setAccountDraft] = useState<AccountDraft | null>(null);
  const [templateDraft, setTemplateDraft] = useState<TemplateDraft | null>(null);
  const [sheets, setSheets] = useState<SampleSheet[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState<'save' | 'sample' | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const request = useRef(0);
  const mounted = useRef(false);
  const sampleInput = useRef<HTMLInputElement>(null);
  const initialDraft = useRef('');

  const load = useCallback(async () => {
    const current = ++request.current; setLoading(true); setLoadError('');
    try {
      const { data } = await api.get(`${SERVER_ADDRESS}store/bank-accounts`, { params: { purpose: 'settings' } });
      if (!mounted.current || current !== request.current) return false;
      if (!Array.isArray(data?.accounts) || !Array.isArray(data?.excel_templates)) throw new Error('Invalid bank settings');
      setAccounts(data.accounts); setTemplates(data.excel_templates); return true;
    } catch (cause) {
      if (mounted.current && current === request.current) setLoadError(errorText(cause, '무통장 설정을 불러오지 못했습니다. 다시 시도해주세요.'));
      return false;
    } finally { if (mounted.current && current === request.current) setLoading(false); }
  }, []);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; request.current += 1; }; }, []);

  const openManager = (mode: 'templates' | 'accounts') => {
    if (disabled || busy) return;
    setOpen(mode); setAccountDraft(null); setTemplateDraft(null); setDiscard(null); setSheets([]); setError(''); setMessage(''); void load();
  };
  const draftSignature = (account: AccountDraft | null, template: TemplateDraft | null) => JSON.stringify({ account, template });
  const hasUnsavedDraft = () => Boolean(accountDraft || templateDraft) && draftSignature(accountDraft, templateDraft) !== initialDraft.current;
  const startAccountDraft = (draft: AccountDraft) => {
    initialDraft.current = draftSignature(draft, null); setAccountDraft(draft); setTemplateDraft(null); setDiscard(null); setError(''); setMessage('');
  };
  const startTemplateDraft = (draft: TemplateDraft) => {
    initialDraft.current = draftSignature(null, draft); setTemplateDraft(draft); setAccountDraft(null); setDiscard(null); setSheets([]);
  };
  const resetEditor = () => {
    initialDraft.current = ''; setAccountDraft(null); setTemplateDraft(null); setDiscard(null); setSheets([]); setError('');
  };
  const leaveEditor = (target: 'dialog' | 'editor') => {
    if (hasUnsavedDraft()) { setDiscard(target); return; }
    resetEditor(); if (target === 'dialog') setOpen(null);
  };
  const closeManager = () => { if (!busy) leaveEditor('dialog'); };
  const mapping = templateDraft?.mapping;
  const sample = sheets.find(sheet => sheet.name === mapping?.sheet_name);
  const headers = sample?.rows[(mapping?.header_row || 1) - 1]?.map(value => value == null ? '' : String(value).trim()) || [];
  const availableHeaders = headers.filter((header, index) => header && headers.indexOf(header) === index && headers.lastIndexOf(header) === index);
  const updateTemplate = (fields: Partial<TemplateDraft>) => templateDraft && setTemplateDraft({ ...templateDraft, ...fields });
  const changeMapping = (fields: Partial<BankExcelMapping>) => updateTemplate({ mapping: {
    sheet_name: '', header_row: 1, depositor_column: '', amount_column: '', ...(mapping || {}), ...fields,
  }});

  const inspect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; event.target.value = '';
    if (!file || !templateDraft || disabled || busy) return;
    if (!/\.xlsx?$/i.test(file.name) || file.size > 5 * 1024 * 1024) { setError('5MB 이하의 .xlsx 또는 .xls 파일을 선택해주세요.'); return; }
    setBusy('sample'); setError(''); setMessage('');
    try {
      const form = new FormData(); form.append('file', file);
      const { data } = await api.post(`${SERVER_ADDRESS}store/bank-accounts/inspect`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!Array.isArray(data?.sheets) || !data.sheets.length) throw new Error('Invalid sample');
      setSheets(data.sheets);
      const first = data.sheets[0];
      updateTemplate({ mapping: { sheet_name: first.name, header_row: 1, depositor_column: '', amount_column: '' } });
      setMessage('샘플을 확인하고 입금자명과 입금액 열을 선택해주세요.');
    } catch (cause) { setError(errorText(cause, '엑셀 샘플을 읽지 못했습니다.')); }
    finally { setBusy(null); }
  };

  const saveTemplate = async () => {
    if (!templateDraft || !mapping || busy) return;
    if (!templateDraft.name.trim() || !mapping.sheet_name || !mapping.depositor_column || !mapping.amount_column) {
      setError('관리 이름과 입금자명·입금액 열을 모두 입력해주세요.'); return;
    }
    if (mapping.depositor_column === mapping.amount_column) { setError('입금자명과 입금액은 서로 다른 열이어야 합니다.'); return; }
    setBusy('save'); setError('');
    const body = { name: templateDraft.name.trim(), mapping };
    try {
      if (templateDraft.seq) await api.patch(`${SERVER_ADDRESS}store/bank-accounts/excel-templates/${templateDraft.seq}`, body, jsonConfig);
      else await api.post(`${SERVER_ADDRESS}store/bank-accounts/excel-templates`, body, jsonConfig);
      initialDraft.current = ''; setTemplateDraft(null); setSheets([]); await load(); setMessage('무통장 정산 엑셀을 저장했습니다.');
    } catch (cause) { setError(errorText(cause, '무통장 정산 엑셀을 저장하지 못했습니다.')); }
    finally { setBusy(null); }
  };

  const saveAccount = async () => {
    if (!accountDraft || busy) return;
    if (![accountDraft.label, accountDraft.bank_name, accountDraft.bank_num, accountDraft.depositor].every(value => value.trim())) {
      setError('계좌 이름, 은행명, 계좌번호, 예금주를 모두 입력해주세요.'); return;
    }
    if (!accountDraft.excel_template_seq) { setError('연결 통장 엑셀을 선택해주세요.'); return; }
    setBusy('save'); setError('');
    const { seq, ...fields } = accountDraft;
    const body = { ...fields, label: fields.label.trim(), bank_name: fields.bank_name.trim(), bank_num: fields.bank_num.trim(), depositor: fields.depositor.trim() };
    try {
      if (seq) await api.patch(`${SERVER_ADDRESS}store/bank-accounts/${seq}`, body, jsonConfig);
      else await api.post(`${SERVER_ADDRESS}store/bank-accounts`, body, jsonConfig);
      initialDraft.current = ''; setAccountDraft(null); await load(); setMessage('통장과 연결 엑셀을 저장했습니다.');
    } catch (cause) { setError(errorText(cause, '통장을 저장하지 못했습니다.')); }
    finally { setBusy(null); }
  };

  return <Section aria-label="무통장 입금 계좌 관리">
    <ConfigBlock>
      <SectionHeading><div><h2>무통장 정산 엑셀 관리</h2><p>은행별 엑셀 열 구성을 여러 개 등록해 통장에 연결합니다.</p></div>
        <ActionButton type="button" disabled={disabled} onClick={() => openManager('templates')}>엑셀 관리</ActionButton></SectionHeading>
    </ConfigBlock>
    <ConfigBlock>
      <SectionHeading><div><h2>통장 설정</h2><p>입금 통장을 등록하고 사용할 정산 엑셀을 선택합니다.</p></div>
        <ActionButton type="button" disabled={disabled} onClick={() => openManager('accounts')}>통장 관리</ActionButton></SectionHeading>
    </ConfigBlock>
    <LiveDetailDialog open={open !== null} onClose={closeManager} title={open === 'templates' ? '무통장 정산 엑셀 관리' : '통장 설정'} size="large" suspended={open === 'accounts' && !!accountDraft}>
      <Manager>
        {loading && <p role="status">설정을 불러오는 중입니다.</p>}
        {loadError && <p role="alert">{loadError} <ActionButton type="button" onClick={load}>다시 불러오기</ActionButton></p>}
        {!loading && !loadError && open === 'templates' && <>
          <ListHeading><h3>등록한 엑셀<span>{templates.length}개</span></h3><ActionButton type="button" data-primary disabled={!!templateDraft} onClick={() => startTemplateDraft(emptyTemplate())}>새 엑셀 등록</ActionButton></ListHeading>
          <AccountList>{templates.map(template => <AccountCard key={template.seq}><AccountCardHeading><AccountTitle><strong>{template.name}</strong></AccountTitle>
            <ActionButton type="button" disabled={!!templateDraft} onClick={() => startTemplateDraft({ seq: template.seq, name: template.name, mapping: { ...template.mapping } })}>수정</ActionButton></AccountCardHeading>
            <AccountMapping><span>{template.mapping.sheet_name} · {template.mapping.header_row}행</span><span>입금자명: {template.mapping.depositor_column} / 입금액: {template.mapping.amount_column}</span></AccountMapping></AccountCard>)}</AccountList>
          {!templates.length && <EmptyState><strong>등록한 엑셀이 없습니다.</strong><p>은행에서 받은 샘플 파일로 정산 엑셀을 등록해주세요.</p></EmptyState>}
          {templateDraft && <Editor><legend>{templateDraft.seq ? '정산 엑셀 수정' : '정산 엑셀 등록'}</legend><EditorContent>
            <Grid><label>관리 이름<input value={templateDraft.name} maxLength={100} onChange={event => updateTemplate({ name: event.target.value })} placeholder="예: 국민은행 거래내역" /></label></Grid>
            <ActionButton type="button" onClick={() => sampleInput.current?.click()}>{busy === 'sample' ? '샘플 확인 중…' : '은행 엑셀 샘플 업로드'}</ActionButton>
            <input ref={sampleInput} type="file" aria-label="은행 엑셀 샘플 파일" accept=".xlsx,.xls" hidden onChange={inspect} />
            {mapping && !sheets.length && <p>저장된 양식: {mapping.sheet_name}, {mapping.header_row}행 · 입금자명: {mapping.depositor_column} · 입금액: {mapping.amount_column}</p>}
            {!!sheets.length && mapping && <><Grid>
              <label>엑셀 시트<select value={mapping.sheet_name} onChange={event => changeMapping({ sheet_name: event.target.value, header_row: 1, depositor_column: '', amount_column: '' })}>{sheets.map(sheet => <option key={sheet.name}>{sheet.name}</option>)}</select></label>
              <label>열 제목 행<input type="number" min={1} max={30} value={mapping.header_row} onChange={event => changeMapping({ header_row: Number(event.target.value), depositor_column: '', amount_column: '' })} /></label>
            </Grid>
            <Preview><table><caption>엑셀 샘플 미리보기</caption><tbody>{sample?.rows.slice(0, 30).map((row, index) => <tr key={index} data-header={index + 1 === mapping.header_row}><th>{index + 1}</th>{row.map((cell, column) => <td key={column}>{cell == null ? '' : String(cell)}</td>)}</tr>)}</tbody></table></Preview>
            <Grid>{mappingFields.map(({ key, label }) => <label key={key}>{label}<select value={mapping[key]} onChange={event => changeMapping({ [key]: event.target.value })}><option value="">열 선택</option>{availableHeaders.map(header => <option key={header}>{header}</option>)}</select></label>)}</Grid></>}
            <EditorActions><ActionButton type="button" onClick={() => leaveEditor('editor')}>취소</ActionButton><ActionButton type="button" data-primary onClick={saveTemplate}>저장</ActionButton></EditorActions>
          </EditorContent></Editor>}
        </>}
        {!loading && !loadError && open === 'accounts' && <>
          <ListHeading><h3>등록한 통장<span>{accounts.length}개</span></h3><ActionButton type="button" data-primary disabled={!!accountDraft} onClick={() => startAccountDraft(emptyAccount())}>새 통장 등록</ActionButton></ListHeading>
          <AccountList>{accounts.map(account => <AccountCard key={account.seq}><AccountCardHeading><AccountTitle><strong>{account.label}</strong><AccountStatus data-active={account.active}>{account.active ? '사용 중' : '사용 중지'}</AccountStatus></AccountTitle>
            <ActionButton type="button" disabled={!!accountDraft} onClick={() => startAccountDraft({ seq: account.seq, label: account.label, bank_name: account.bank_name, bank_num: account.bank_num, depositor: account.depositor, active: account.active, excel_template_seq: account.excel_template_seq ?? null })}>수정</ActionButton></AccountCardHeading>
            <AccountDetails><div><dt>은행명</dt><dd>{account.bank_name}</dd></div><div><dt>계좌번호</dt><dd>{account.bank_num}</dd></div><div><dt>예금주</dt><dd>{account.depositor}</dd></div></AccountDetails>
            <AccountMapping><span>{templates.find(template => template.seq === account.excel_template_seq)?.name || '연결 엑셀 미설정'}</span></AccountMapping></AccountCard>)}</AccountList>
          {!accounts.length && <EmptyState><strong>등록한 통장이 없습니다.</strong></EmptyState>}
        </>}
        {!accountDraft && error && <p role="alert">{error}</p>}{!accountDraft && message && <p role="status">{message}</p>}
        {!accountDraft && discard && <DiscardNotice role="alert"><p>저장하지 않은 설정이 있습니다. 변경 내용을 버릴까요?</p><Actions>
          <ActionButton type="button" onClick={() => setDiscard(null)}>계속 편집</ActionButton>
          <ActionButton type="button" data-danger onClick={() => { const target = discard; resetEditor(); if (target === 'dialog') setOpen(null); }}>변경 내용 버리기</ActionButton>
        </Actions></DiscardNotice>}
      </Manager>
    </LiveDetailDialog>
    <LiveDetailDialog open={open === 'accounts' && !!accountDraft} onClose={() => { if (!busy) leaveEditor('editor'); }} title={accountDraft?.seq ? '통장 수정' : '통장 등록'} size="medium">
      <Manager>
        {accountDraft && <ModalEditor><EditorContent><Grid>
          <label>계좌 이름<input value={accountDraft.label} onChange={event => setAccountDraft({ ...accountDraft, label: event.target.value })} /></label>
          <label>은행명<input value={accountDraft.bank_name} onChange={event => setAccountDraft({ ...accountDraft, bank_name: event.target.value })} /></label>
          <label>계좌번호<input value={accountDraft.bank_num} inputMode="numeric" onChange={event => setAccountDraft({ ...accountDraft, bank_num: event.target.value })} /></label>
          <label>예금주<input value={accountDraft.depositor} onChange={event => setAccountDraft({ ...accountDraft, depositor: event.target.value })} /></label>
          <label>연결 통장 엑셀 *<select required aria-label="연결 통장 엑셀" value={accountDraft.excel_template_seq ?? ''} onChange={event => setAccountDraft({ ...accountDraft, excel_template_seq: event.target.value ? Number(event.target.value) : null })}><option value="">선택해주세요</option>{templates.map(template => <option key={template.seq} value={template.seq}>{template.name}</option>)}</select></label>
        </Grid><AccountUsage><span>계좌 사용</span><UsageOptions><UsageButton type="button" aria-pressed={accountDraft.active} onClick={() => setAccountDraft({ ...accountDraft, active: true })}>사용</UsageButton><UsageButton type="button" aria-pressed={!accountDraft.active} onClick={() => setAccountDraft({ ...accountDraft, active: false })}>사용 중지</UsageButton></UsageOptions></AccountUsage>
        <EditorActions><ActionButton type="button" onClick={() => leaveEditor('editor')}>취소</ActionButton><ActionButton type="button" data-primary onClick={saveAccount}>저장</ActionButton></EditorActions></EditorContent></ModalEditor>}
        {error && <p role="alert">{error}</p>}
        {discard === 'editor' && <DiscardNotice role="alert"><p>저장하지 않은 설정이 있습니다. 변경 내용을 버릴까요?</p><Actions>
          <ActionButton type="button" onClick={() => setDiscard(null)}>계속 편집</ActionButton>
          <ActionButton type="button" data-danger onClick={resetEditor}>변경 내용 버리기</ActionButton>
        </Actions></DiscardNotice>}
      </Manager>
    </LiveDetailDialog>
  </Section>;
}

const ConfigBlock = styled.div`
  & + & { margin-top: var(--space-6); padding-top: var(--space-6); border-top: 1px solid var(--color-border-default); }
`;

const Section = styled.section`
  margin-top: var(--space-6);
  padding-top: var(--space-6);
  border-top: 1px solid var(--color-border-default);
  h2 { margin: 0; font-size: var(--text-h3); font-weight: var(--font-weight-semibold); }
  p { margin: var(--space-2) 0 0; color: var(--color-text-secondary); font-size: var(--text-body); line-height: var(--leading-body); }
`;
const SectionHeading = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); flex-wrap: wrap;
`;
const ActionButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  min-height: var(--control-lg); padding: 0 var(--space-5);
  border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
  background: var(--color-bg-surface); color: var(--color-text-primary);
  font: var(--font-weight-medium) var(--text-body-lg)/var(--leading-body-lg) var(--font-family-base);
  cursor: pointer;
  &:hover:not(:disabled) { background: var(--color-bg-subtle); border-color: var(--color-border-strong); }
  &[data-primary='true'] { background: var(--gradient-brand); color: var(--color-text-on-brand); border-color: transparent; }
  &[data-primary='true']:hover:not(:disabled) { background: var(--gradient-brand-hover); }
  &[data-danger='true'] { color: var(--color-text-danger); border-color: var(--color-border-danger); }
  &:disabled { background: var(--color-bg-muted); color: var(--color-text-disabled); cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--color-text-link); outline-offset: 2px; }
`;
const Manager = styled.div`
  color: var(--color-text-primary); font-family: var(--font-family-base);
  h3 { font-size: var(--text-h3); margin: var(--space-5) 0 var(--space-2); }
  p { font-size: var(--text-body); line-height: var(--leading-body); color: var(--color-text-secondary); }
  [role='alert'] { color: var(--color-text-danger); }
  input:focus-visible, select:focus-visible { outline: 2px solid var(--color-text-link); outline-offset: 2px; }
`;
const ListHeading = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); flex-wrap: wrap;
  h3 { margin: 0; display: flex; gap: var(--space-2); align-items: center; }
  span { color: var(--color-text-secondary); font-size: var(--text-body); font-weight: var(--font-weight-regular); }
`;
const AccountList = styled.ul`
  display: flex; flex-direction: column; gap: var(--space-3); padding: 0; margin: var(--space-4) 0 0; list-style: none;
`;
const AccountCard = styled.li`
  display: flex; flex-direction: column; gap: var(--space-4); min-width: 0; padding: var(--space-4);
  border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface);
  &[data-editing='true'] { border-color: var(--color-brand); }
`;
const AccountCardHeading = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: var(--space-3);
`;
const AccountTitle = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2); min-width: 0; overflow-wrap: anywhere;
  strong { font-size: var(--text-body-lg); font-weight: var(--font-weight-semibold); }
`;
const AccountStatus = styled.span`
  flex-shrink: 0; padding: var(--space-1) var(--space-2); border-radius: var(--radius-sm);
  background: var(--color-bg-muted); color: var(--color-text-secondary); font-size: var(--text-caption); font-weight: var(--font-weight-medium);
  &[data-active='true'] { background: var(--color-success-subtle); color: var(--color-success); }
`;
const AccountDetails = styled.dl`
  display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr); gap: var(--space-4); margin: 0;
  > div { display: flex; flex-direction: column; gap: var(--space-1); min-width: 0; }
  dt { font-size: var(--text-caption); color: var(--color-text-secondary); }
  dd { margin: 0; font-size: var(--text-body); color: var(--color-text-primary); overflow-wrap: anywhere; }
  .account-number { font-variant-numeric: tabular-nums; font-weight: var(--font-weight-medium); }
  @media (max-width: 767px) { grid-template-columns: 1fr; gap: var(--space-3); }
`;
const AccountMapping = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2); padding-top: var(--space-3);
  border-top: 1px solid var(--color-border-default); color: var(--color-text-secondary); font-size: var(--text-caption); overflow-wrap: anywhere;
`;
const EmptyState = styled.div`
  margin-top: var(--space-4); padding: var(--space-8) var(--space-4); text-align: center;
  border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-subtle);
`;
const Actions = styled.div`display: flex; justify-content: flex-end; flex-wrap: wrap; gap: var(--space-2); margin: var(--space-4) 0;`;
const DiscardNotice = styled.div`
  margin-top: var(--space-5); padding: var(--space-4);
  border: 1px solid var(--color-border-danger); border-radius: var(--radius-md); background: var(--color-danger-subtle);
  p { margin: 0; color: var(--color-text-danger); }
  ${Actions} { margin-bottom: 0; }
`;
const Editor = styled.fieldset`
  padding: var(--space-5) 0 0; margin: var(--space-6) 0 0; min-width: 0; border: 0; border-top: 1px solid var(--color-border-default);
  legend { font-weight: var(--font-weight-semibold); padding-right: var(--space-3); }
`;
const ModalEditor = styled(Editor)`
  margin: 0; padding: 0; border: 0;
`;
const EditorContent = styled.div`
  display: flex; flex-direction: column; gap: var(--space-6); padding-top: var(--space-4);
  p, h3 { margin: 0; }
`;
const AccountUsage = styled.div`
  display: flex; flex-direction: column; align-items: flex-start; gap: var(--space-2);
  font-size: var(--text-body);
`;
const UsageOptions = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-2);
`;
const UsageButton = styled(ActionButton)`
  gap: var(--gap-icon);
  svg { width: var(--space-4); height: var(--space-4); flex-shrink: 0; visibility: hidden; }
  &[aria-pressed='true'] { background: var(--color-bg-inverse); color: var(--color-text-on-inverse); border-color: var(--color-bg-inverse); }
  &[aria-pressed='true']:hover:not(:disabled) { background: var(--color-bg-inverse-hover); border-color: var(--color-bg-inverse-hover); }
  &[aria-pressed='true'] svg { visibility: visible; }
  &:disabled { background: var(--color-bg-muted); color: var(--color-text-disabled); border-color: var(--color-border-default); }
`;
const EditorActions = styled(Actions)`
  margin: 0; padding-top: var(--space-6); border-top: 1px solid var(--color-border-default);
  @media (max-width: 767px) { flex-direction: column-reverse; > button { width: 100%; } }
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-4) var(--space-3);
  label { display: flex; flex-direction: column; gap: var(--space-2); min-width: 0; font-size: var(--text-body); }
  input, select { box-sizing: border-box; width: 100%; min-width: 0; min-height: var(--control-lg); padding: var(--space-3); border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface); color: var(--color-text-primary); font-size: var(--text-body-lg); }
  @media (max-width: 767px) { grid-template-columns: 1fr; }
`;
const Preview = styled.div`
  overflow: auto; max-height: 260px;
  table { border-collapse: collapse; width: 100%; text-align: left; font-size: var(--text-body); }
  caption { text-align: left; padding-bottom: var(--space-2); }
  th, td { border: 1px solid var(--color-border-table); padding: var(--space-2); min-width: 80px; }
  tr[data-header='true'] { background: var(--color-brand-subtle); font-weight: var(--font-weight-semibold); }
`;
